import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Check as CheckIcon, CreditCard as CreditCardIcon } from '@mui/icons-material'
import {
  useGetCurrentSubscriptionQuery,
  useGetPlansQuery,
  useSelectPlanMutation,
} from '@store/api/subscriptionApi'
import { useAppDispatch } from '@store/hooks'
import { useBetaMode } from '@hooks/useBetaMode'
import { updateUser } from '@store/slices/authSlice'

type BillingPeriod = 'monthly' | 'annual'

const SubscriptionPlans: React.FC = () => {
  const dispatch = useAppDispatch()
  const { beta } = useBetaMode()
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly')
  const [cancelOpen, setCancelOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const { data: plans, isLoading: plansLoading, error: plansError } = useGetPlansQuery()
  const { data: currentSubscription, isLoading: currentLoading, error: currentError } =
    useGetCurrentSubscriptionQuery()
  const [selectPlan, { isLoading: selectingPlan }] = useSelectPlanMutation()

  const syncAuthTier = (plan: 'free' | 'premium' | 'pro') => {
    dispatch(updateUser({ subscriptionTier: plan }))
  }

  const handleSelectPlan = async (planName: string) => {
    try {
      const result = await selectPlan({ plan: planName as 'free' | 'premium' | 'pro' }).unwrap()
      if (!result.waitlisted) {
        syncAuthTier(result.plan)
      }
      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success',
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message: (error as { data?: { message?: string } })?.data?.message || 'Failed to select plan',
        severity: 'error',
      })
    }
  }

  const handleCancel = async () => {
    try {
      const result = await selectPlan({ plan: 'free' }).unwrap()
      syncAuthTier(result.plan)
      setCancelOpen(false)
      setSnackbar({ open: true, message: result.message, severity: 'success' })
    } catch (error) {
      setSnackbar({
        open: true,
        message: (error as { data?: { message?: string } })?.data?.message || 'Failed to cancel subscription',
        severity: 'error',
      })
    }
  }

  const isCurrentPlan = (planName: string) => currentSubscription?.plan === planName

  if (plansLoading || currentLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    )
  }

  if (plansError || currentError) {
    return (
      <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
        <Alert severity="error">Failed to load subscription plans. Please try again later.</Alert>
      </Box>
    )
  }

  if (!plans || !currentSubscription) {
    return (
      <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
        <Alert severity="warning">Subscription information unavailable.</Alert>
      </Box>
    )
  }

  const planActionLabel = (planName: string, isCurrent: boolean): string => {
    if (isCurrent) return 'Current plan'
    if (planName === 'free') {
      return currentSubscription.canDowngrade ? 'Use Downgrade above' : 'Switch to Free'
    }
    return 'Join waitlist'
  }

  const isPlanActionDisabled = (planName: string, isCurrent: boolean): boolean => {
    if (isCurrent || selectingPlan) return true
    if (planName === 'free' && currentSubscription.canDowngrade) return true
    return false
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View your plan, compare tiers, and manage billing preferences.
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <CreditCardIcon color="primary" sx={{ fontSize: 40 }} />
            <Box flex={1}>
              <Typography variant="overline" color="text.secondary">
                Current plan
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {currentSubscription.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentSubscription.plan === 'free'
                  ? 'No charge — includes core collection features'
                  : `$${currentSubscription.monthlyPrice.toFixed(2)}/month list price`}
              </Typography>
            </Box>
            <Chip label={currentSubscription.plan} color="primary" variant="outlined" />
          </Stack>

          {!currentSubscription.billingIntegrated && currentSubscription.plan !== 'free' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Paid billing is not integrated yet. Your tier may have been assigned by an administrator.
            </Alert>
          )}

          {currentSubscription.canDowngrade && (
            <Box sx={{ mt: 2 }}>
              <Button color="warning" variant="outlined" onClick={() => setCancelOpen(true)}>
                Downgrade to Free
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Compare plans
        </Typography>
        <ToggleButtonGroup
          value={billingPeriod}
          exclusive
          onChange={(_e, newPeriod) => {
            if (newPeriod !== null) setBillingPeriod(newPeriod)
          }}
          size="small"
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="annual">Annual (Save 17%)</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {plans.map((plan) => {
          const isCurrent = isCurrentPlan(plan.name)
          const price = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice
          const billingLabel = billingPeriod === 'monthly' ? '/month' : '/year'

          return (
            <Grid item xs={12} md={4} key={plan.name}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: isCurrent ? 2 : 1,
                  borderColor: isCurrent ? 'primary.main' : 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight={600}>
                      {plan.displayName}
                    </Typography>
                    {isCurrent && <Chip label="Current" size="small" color="primary" />}
                  </Stack>
                  <Typography variant="h4" color="primary.main" fontWeight={700}>
                    ${price.toFixed(2)}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {billingLabel}
                    </Typography>
                  </Typography>
                  <List dense sx={{ mt: 2 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28, color: 'success.main' }}>
                          <CheckIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={isCurrent ? 'outlined' : 'contained'}
                    disabled={isPlanActionDisabled(plan.name, isCurrent)}
                    onClick={() => handleSelectPlan(plan.name)}
                  >
                    {planActionLabel(plan.name, isCurrent)}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      {!beta && (
        <Alert severity="info">
          Premium and Pro checkout is coming soon. Joining the waitlist does not change your current tier until billing
          launches or an admin assigns a plan.
        </Alert>
      )}

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Downgrade to Free?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You will lose premium-tier limits and features tied to {currentSubscription.displayName}. You can rejoin a
            paid plan later when billing is available.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep plan</Button>
          <Button color="warning" variant="contained" onClick={handleCancel} disabled={selectingPlan}>
            {selectingPlan ? 'Downgrading…' : 'Downgrade to Free'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SubscriptionPlans
